import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import router from 'next/router';
import fetchNext from '@features/api/fetchNext';
import BaseStore from '@context/global/mobx/BaseStore';
import { ITaxRegimeData, TAddressTypeV2 } from '@features/userPanel/typescript';
import logger from '@utils/logger';
import NextCustomerApi from '@features/api/nextApiService/nextCustomerApi';
import { DICTIONARY_TAX_REGIME_TYPE, DICTIONARY_COUNTRIES_TYPE } from '@features/checkout/const';
import { addressesOfType, getBaseAddress } from '@features/checkout/mobx/stores/helpers';
import { IBasicAddress, TDictionaryCountry } from '@features/checkout/typescript';
import checkoutService from '@features/checkout/services/checkoutService';
import { getRegion } from '@utils/misc';
import { TRegion } from '@typescript/index';
import { AVAILABLE_REGIONS } from '@const/index';
export type AddressFormType = 'addNewAddress' | 'editAddress' | 'addBillingData' | null;
class UserPanelAddressesStore extends BaseStore {
  userAddresses = observable(new Array<IBasicAddress>());
  isLoading = false;
  formOpened: AddressFormType = null;
  selectedAddress: IBasicAddress | null = null;
  selectedBilling: IBasicAddress | null = null;
  changedAddressId: number | null = null;
  baseAddressConfirmationMessage: string | null = null;
  deliveryAddressConfirmationMessage: string | null = null;
  billingAddressConfirmationMessage: string | null = null;
  taxRegimeData: Array<ITaxRegimeData> | null = null;
  availableCountries: TDictionaryCountry[] = [];
  constructor() {
    super();
    makeObservable(this, {
      userAddresses: observable,
      isLoading: observable,
      formOpened: observable,
      selectedAddress: observable,
      selectedBilling: observable,
      baseAddressConfirmationMessage: observable,
      deliveryAddressConfirmationMessage: observable,
      billingAddressConfirmationMessage: observable,
      changedAddressId: observable,
      loadAddresses: action,
      deleteAddress: action,
      openForm: action,
      openBillingFormAction: action,
      closeFormAction: action,
      showConfirmationMessage: action,
      showBillingConfirmationMessage: action,
      closeConfirmationMessage: action,
      replaceBasicWithDeliveryAddress: action,
      baseAddress: computed,
      deliveryAddresses: computed,
      invoiceAddresses: computed,
      taxRegimeData: observable,
      availableCountries: observable
    });
    this.loadAddresses();
  }
  loadAddresses = this.withProgress(async () => {
    const {
      locale
    } = router;
    this.isLoading = true;
    const {
      json
    } = await fetchNext('customer/addresses', {
      method: 'GET'
    }, locale?.toLocaleLowerCase());
    const region = getRegion('uppercase') as TRegion;
    if (region === AVAILABLE_REGIONS.mexico) {
      this.taxRegimeData = await NextCustomerApi.getDictionaryType(DICTIONARY_TAX_REGIME_TYPE);
    }
    this.availableCountries = await NextCustomerApi.getDictionaryType(DICTIONARY_COUNTRIES_TYPE);
    runInAction(() => {
      this.isLoading = false;
      if (json) {
        this.userAddresses.replace(json);
      }
    });
    // return this.userAddresses;
  });
  deleteAddress = this.withProgress(async (addressId: number, addressType: TAddressTypeV2) => {
    this.isLoading = true;
    try {
      await NextCustomerApi.deleteUserAddress(addressId);
      this.loadAddresses();
      runInAction(() => {
        const addressTypeValue = TAddressTypeV2.invoice;
        if (addressType === addressTypeValue) {
          this.billingAddressConfirmationMessage = 'dataDeleted';
        } else {
          this.deliveryAddressConfirmationMessage = 'dataDeleted';
        }
      });
    } catch (e) {
      logger.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
    }
  });
  get baseAddress(): IBasicAddress | null {
    return getBaseAddress(this.userAddresses);
  }
  get deliveryAddresses(): Array<IBasicAddress> | [] {
    return addressesOfType(this.userAddresses, TAddressTypeV2.delivery);
  }
  get invoiceAddresses(): Array<IBasicAddress> | [] {
    const otherAddressList = addressesOfType(this.userAddresses, TAddressTypeV2.invoice);
    const typeAdditionalData = 'taxRegime';
    const otherAddressListWithAdditionalData = otherAddressList.map((otherAddress: IBasicAddress) => {
      if (this.taxRegimeData && otherAddress.additionalData) {
        const objFind = otherAddress.additionalData[typeAdditionalData] ? otherAddress.additionalData : null;
        if (!objFind) return otherAddress;
        const [taxRegime] = this.taxRegimeData.filter(key => key.code === objFind[typeAdditionalData]);
        return {
          ...otherAddress,
          taxRegime
        };
      }
      return otherAddress;
    });
    return otherAddressListWithAdditionalData || [];
  }
  public openForm = action((formType: AddressFormType, address: IBasicAddress | null) => {
    this.formOpened = formType;
    if (address) {
      this.selectedAddress = address;
    }
  });
  public openBillingFormAction = action((formType: AddressFormType, userBillingType: IBasicAddress | null) => {
    this.formOpened = formType;
    if (userBillingType) {
      this.selectedBilling = userBillingType;
    }
  });
  public closeFormAction = action(() => {
    runInAction(() => {
      this.formOpened = null;
      this.selectedAddress = null;
      this.selectedBilling = null;
    });
  });
  public closeFormWithoutSaving = action(() => {
    runInAction(() => {
      this.formOpened = null;
      this.selectedAddress = null;
      this.selectedBilling = null;
      this.baseAddressConfirmationMessage = null;
      this.deliveryAddressConfirmationMessage = null;
      this.billingAddressConfirmationMessage = null;
    });
  });
  public showConfirmationMessage = action((addressId: number | null) => {
    const lastSelectedType = this.selectedAddress?.type;
    this.closeConfirmationMessage();
    const message = {
      addBillingData: 'addBillingData',
      addNewAddress: 'addressAdded',
      editAddress: 'dataChanged'
    };
    runInAction(() => {
      const basicAddressType = TAddressTypeV2.base;
      const deliveryAddressType = TAddressTypeV2.delivery;
      if (lastSelectedType === basicAddressType) {
        this.baseAddressConfirmationMessage = this.formOpened && message[this.formOpened] || null;
      } else if (lastSelectedType === deliveryAddressType) {
        this.deliveryAddressConfirmationMessage = this.formOpened && message[this.formOpened] || null;
      } else {
        this.deliveryAddressConfirmationMessage = this.formOpened && message[this.formOpened] || null;
      }
    });
    this.changedAddressId = addressId;
  });
  public showBillingConfirmationMessage = action((addressId: number | null) => {
    const message = {
      addBillingData: 'addBillingData',
      editAddress: 'dataChanged'
    };
    this.changedAddressId = addressId;
    runInAction(() => {
      this.billingAddressConfirmationMessage = this.formOpened && message[this.formOpened] || null;
    });
  });
  public closeConfirmationMessage = action(() => {
    runInAction(() => {
      this.baseAddressConfirmationMessage = null;
      this.deliveryAddressConfirmationMessage = null;
      this.billingAddressConfirmationMessage = null;
      this.selectedAddress = null;
      this.selectedBilling = null;
      this.changedAddressId = null;
    });
  });
  public replaceBasicWithDeliveryAddress = this.withProgress(async (address: IBasicAddress): Promise<void> => {
    const basic = this.baseAddress;
    if (basic && basic.id && address.id) {
      const deliveryAddressType = TAddressTypeV2.delivery;
      const basicAddressType = TAddressTypeV2.base;
      const changeBasic = checkoutService.changeAddress(basic.id, {
        ...address,
        email: basic.email,
        type: basicAddressType
      });
      const changeDelivery = checkoutService.changeAddress(address.id, {
        ...basic,
        email: address.email,
        type: deliveryAddressType
      });
      await Promise.all([changeBasic, changeDelivery]);
    }
    return this.loadAddresses();
  });
}
export default UserPanelAddressesStore;